import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, CalendarIcon, TagIcon, UserIcon, FolderIcon, FileIcon, CheckIcon, XIcon } from 'lucide-react';
export function FileFilters({
  onFilterChange,
  locale = 'en-US',
  isAdminView = false
}) {
  const [filters, setFilters] = useState({
    search: '',
    fileType: 'all',
    department: 'all',
    dateRange: '',
    startDate: '',
    endDate: '',
    uploadedBy: 'all',
    tags: [],
    status: 'all'
  });
  const [availableTags, setAvailableTags] = useState(['financial', 'quarterly', 'report', 'marketing', 'campaign', 'presentation', 'budget', 'hr', 'handbook', 'policies', 'product', 'roadmap', 'planning', 'guidelines', 'compliance', 'images', 'it', 'security', 'protocols', 'customer', 'feedback', 'board', 'meeting', 'minutes']);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  // File type options
  const fileTypes = [{
    value: 'all',
    label: 'All File Types'
  }, {
    value: 'pdf',
    label: 'PDF Documents'
  }, {
    value: 'docx',
    label: 'Word Documents'
  }, {
    value: 'xlsx',
    label: 'Excel Spreadsheets'
  }, {
    value: 'pptx',
    label: 'PowerPoint Presentations'
  }, {
    value: 'zip',
    label: 'Compressed Archives'
  }];
  // Department options
  const departments = [{
    value: 'all',
    label: 'All Departments'
  }, {
    value: 'Finance',
    label: 'Finance'
  }, {
    value: 'HR',
    label: 'Human Resources'
  }, {
    value: 'Marketing',
    label: 'Marketing'
  }, {
    value: 'IT',
    label: 'IT'
  }, {
    value: 'Product',
    label: 'Product'
  }, {
    value: 'Customer Support',
    label: 'Customer Support'
  }, {
    value: 'Executive',
    label: 'Executive'
  }];
  // Uploader options (would come from API in real app)
  const uploaders = [{
    value: 'all',
    label: 'All Uploaders'
  }, {
    value: 'John Smith',
    label: 'John Smith'
  }, {
    value: 'Sarah Johnson',
    label: 'Sarah Johnson'
  }, {
    value: 'Michael Brown',
    label: 'Michael Brown'
  }, {
    value: 'David Wilson',
    label: 'David Wilson'
  }, {
    value: 'Jennifer Lee',
    label: 'Jennifer Lee'
  }, {
    value: 'Robert Taylor',
    label: 'Robert Taylor'
  }, {
    value: 'Amanda Clark',
    label: 'Amanda Clark'
  }, {
    value: 'Charles Martin',
    label: 'Charles Martin'
  }];
  // Status options
  const statuses = [{
    value: 'all',
    label: 'All Statuses'
  }, {
    value: 'Active',
    label: 'Active'
  }, {
    value: 'Archived',
    label: 'Archived'
  }, {
    value: 'Deleted',
    label: 'Deleted'
  }];
  // Date range options
  const dateRanges = [{
    value: '',
    label: 'Any Time'
  }, {
    value: 'last7days',
    label: 'Last 7 Days'
  }, {
    value: 'last30days',
    label: 'Last 30 Days'
  }, {
    value: 'last90days',
    label: 'Last 90 Days'
  }, {
    value: 'custom',
    label: 'Custom Range'
  }];
  // Filter tags based on input
  useEffect(() => {
    if (tagInput) {
      const filtered = availableTags.filter(tag => tag.toLowerCase().includes(tagInput.toLowerCase()) && !selectedTags.includes(tag));
      setFilteredTags(filtered);
    } else {
      setFilteredTags([]);
    }
  }, [tagInput, selectedTags, availableTags]);
  // Apply filters when they change
  useEffect(() => {
    const updatedFilters = {
      ...filters,
      tags: selectedTags
    };
    onFilterChange(updatedFilters);
  }, [filters, selectedTags]);
  // Handle input changes
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  // Handle date range change
  const handleDateRangeChange = e => {
    const {
      value
    } = e.target;
    setFilters({
      ...filters,
      dateRange: value,
      // Reset custom dates if not using custom range
      ...(value !== 'custom' && {
        startDate: '',
        endDate: ''
      })
    });
  };
  // Handle tag selection
  const handleTagSelect = tag => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setTagInput('');
  };
  // Handle tag removal
  const handleTagRemove = tag => {
    setSelectedTags(selectedTags.filter(t => t !== tag));
  };
  // Handle tag input
  const handleTagInputChange = e => {
    setTagInput(e.target.value);
  };
  // Handle tag input key press (for Enter)
  const handleTagInputKeyPress = e => {
    if (e.key === 'Enter' && tagInput) {
      e.preventDefault();
      if (filteredTags.length > 0) {
        handleTagSelect(filteredTags[0]);
      } else if (!selectedTags.includes(tagInput)) {
        // Add new tag if it doesn't exist
        setSelectedTags([...selectedTags, tagInput]);
        setTagInput('');
      }
    }
  };
  // Reset filters
  const handleReset = () => {
    setFilters({
      search: '',
      fileType: 'all',
      department: 'all',
      dateRange: '',
      startDate: '',
      endDate: '',
      uploadedBy: 'all',
      tags: [],
      status: 'all'
    });
    setSelectedTags([]);
    setTagInput('');
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-5163">
      <div className="px-4 py-5 sm:p-6" data-id="element-5164">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-5165">
          {/* Search */}
          <div className="sm:col-span-6" data-id="element-5166">
            <div className="mt-1 flex rounded-md shadow-sm" data-id="element-5167">
              <div className="relative flex-grow focus-within:z-10" data-id="element-5168">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5169">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-5170" />
                </div>
                <input type="text" name="search" id="search" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-md pl-10 sm:text-sm border-gray-300" placeholder="Search files by name, uploader, department, or tags" value={filters.search} onChange={handleInputChange} data-id="element-5171" />
              </div>
            </div>
          </div>

          {/* File Type */}
          <div className="sm:col-span-2" data-id="element-5172">
            <label htmlFor="fileType" className="block text-sm font-medium text-gray-700" data-id="element-5173">
              File Type
            </label>
            <select id="fileType" name="fileType" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.fileType} onChange={handleInputChange} data-id="element-5174">
              {fileTypes.map(type => <option key={type.value} value={type.value} data-id="element-5175">
                  {type.label}
                </option>)}
            </select>
          </div>

          {/* Department */}
          <div className="sm:col-span-2" data-id="element-5176">
            <label htmlFor="department" className="block text-sm font-medium text-gray-700" data-id="element-5177">
              Department
            </label>
            <select id="department" name="department" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.department} onChange={handleInputChange} data-id="element-5178">
              {departments.map(dept => <option key={dept.value} value={dept.value} data-id="element-5179">
                  {dept.label}
                </option>)}
            </select>
          </div>

          {/* Uploaded By */}
          <div className="sm:col-span-2" data-id="element-5180">
            <label htmlFor="uploadedBy" className="block text-sm font-medium text-gray-700" data-id="element-5181">
              Uploaded By
            </label>
            <select id="uploadedBy" name="uploadedBy" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.uploadedBy} onChange={handleInputChange} data-id="element-5182">
              {uploaders.map(uploader => <option key={uploader.value} value={uploader.value} data-id="element-5183">
                  {uploader.label}
                </option>)}
            </select>
          </div>

          {/* Date Range */}
          <div className="sm:col-span-2" data-id="element-5184">
            <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700" data-id="element-5185">
              Date Range
            </label>
            <select id="dateRange" name="dateRange" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.dateRange} onChange={handleDateRangeChange} data-id="element-5186">
              {dateRanges.map(range => <option key={range.value} value={range.value} data-id="element-5187">
                  {range.label}
                </option>)}
            </select>
          </div>

          {/* Custom Date Range */}
          {filters.dateRange === 'custom' && <>
              <div className="sm:col-span-2" data-id="element-5188">
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700" data-id="element-5189">
                  Start Date
                </label>
                <div className="mt-1" data-id="element-5190">
                  <input type="date" name="startDate" id="startDate" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={filters.startDate} onChange={handleInputChange} data-id="element-5191" />
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-5192">
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700" data-id="element-5193">
                  End Date
                </label>
                <div className="mt-1" data-id="element-5194">
                  <input type="date" name="endDate" id="endDate" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={filters.endDate} onChange={handleInputChange} data-id="element-5195" />
                </div>
              </div>
            </>}

          {/* Status */}
          {isAdminView && <div className="sm:col-span-2" data-id="element-5196">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-5197">
                Status
              </label>
              <select id="status" name="status" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={handleInputChange} data-id="element-5198">
                {statuses.map(status => <option key={status.value} value={status.value} data-id="element-5199">
                    {status.label}
                  </option>)}
              </select>
            </div>}

          {/* Tags */}
          <div className="sm:col-span-6" data-id="element-5200">
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700" data-id="element-5201">
              Tags
            </label>
            <div className="mt-1" data-id="element-5202">
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-5203">
                {selectedTags.map(tag => <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-5204">
                    <TagIcon className="h-3 w-3 mr-1" data-id="element-5205" />
                    {tag}
                    <button type="button" className="ml-1 flex-shrink-0 h-4 w-4 rounded-full inline-flex items-center justify-center text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500 focus:outline-none focus:bg-indigo-500 focus:text-white" onClick={() => handleTagRemove(tag)} data-id="element-5206">
                      <span className="sr-only" data-id="element-5207">Remove tag {tag}</span>
                      <XIcon className="h-2 w-2" data-id="element-5208" />
                    </button>
                  </span>)}
              </div>
              <div className="relative" data-id="element-5209">
                <input type="text" id="tags" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Type to add tags" value={tagInput} onChange={handleTagInputChange} onKeyPress={handleTagInputKeyPress} data-id="element-5210" />
                {filteredTags.length > 0 && <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm" data-id="element-5211">
                    <ul data-id="element-5212">
                      {filteredTags.map(tag => <li key={tag} className="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900 hover:bg-indigo-50" onClick={() => handleTagSelect(tag)} data-id="element-5213">
                          <div className="flex items-center" data-id="element-5214">
                            <TagIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5215" />
                            <span className="block truncate" data-id="element-5216">{tag}</span>
                          </div>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="mt-6 flex justify-end" data-id="element-5217">
          <button type="button" onClick={handleReset} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5218">
            <XIcon className="h-4 w-4 mr-1" data-id="element-5219" />
            Reset Filters
          </button>
        </div>
      </div>
    </div>;
}